// NCB calls flow through the backend proxy — secret key never ships to the browser.
// Disabling NCB on this project (server-side flag) cuts off all calls instantly.
const NCB_CONFIG = {
  conversationId: 'chat-ff294557-fc44-4975-9bfe-cd0cd87e0a78',
  proxyBase: 'https://rmvzorxcl35mttidiexhtp5g2m0hpsqo.lambda-url.us-east-2.on.aws',
  instance: '48568_bis_db',
};

// 🚨 ABSOLUTE RULE: every NCB proxy request MUST carry ?Instance=<instance>.
// Without it the proxy cannot route the call and will return an error.
// buildProxyUrl enforces this — always use it; never construct proxy URLs manually.
function buildProxyUrl(path) {
  const url = new URL(path, NCB_CONFIG.proxyBase);
  if (NCB_CONFIG.instance) {
    url.searchParams.set('Instance', NCB_CONFIG.instance);
  }
  return url.toString();
}

// Track if we are currently refreshing the session
let isRefreshing = false;

// All helpers return a uniform envelope: { status, data, ... }.
// Data endpoints return { status, data, metadata }; auth endpoints (flat NCB responses) are wrapped to the same shape.
// On network or non-2xx errors returns { status: 'error', data: null, error, httpStatus } — never throws.
async function ncbRequest(url, options = {}, bypassInterceptor = false) {
  try {
    const res = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    
    const body = await res.json().catch(() => ({}));
    
    if (!res.ok) {
      // If the session cookie is truly expired, the backend returns 401.
      if (res.status === 401 && !bypassInterceptor) {
        
        // If not already refreshing, trigger the background refresh
        if (!isRefreshing) {
          isRefreshing = true;
          window.dispatchEvent(new Event('auth-session-expired'));
        }

        // Return a promise that queues the request and waits for the session to be restored or to fail
        return new Promise((resolve) => {
          const onRestored = async () => {
             cleanup();
             isRefreshing = false; // Reset the flag
             // Retry the original request bypassing the interceptor (so we don't loop forever if it fails again)
             resolve(await ncbRequest(url, options, true));
          };

          const onFailed = () => {
             cleanup();
             isRefreshing = false; // Reset the flag
             // Resolve with the original 401 error since re-login failed
             resolve({ status: 'error', data: null, error: body?.message || body?.error || `HTTP ${res.status}`, httpStatus: res.status });
          };

          const cleanup = () => {
             window.removeEventListener('auth-session-restored', onRestored);
             window.removeEventListener('auth-session-failed', onFailed);
          };

          window.addEventListener('auth-session-restored', onRestored);
          window.addEventListener('auth-session-failed', onFailed);
        });
      }

      return { status: 'error', data: null, error: body?.message || body?.error || `HTTP ${res.status}`, httpStatus: res.status };
    }
    
    // Data endpoints already return { status, data, metadata } — pass through.
    // Auth endpoints return flat objects ({ session, user } | { redirect, token, user } | ...) — wrap them.
    if (body && typeof body === 'object' && !Array.isArray(body) && typeof body.status === 'string' && 'data' in body) {
      return body;
    }
    return { status: 'success', data: body };
  } catch (err) {
    return { status: 'error', data: null, error: err?.message || 'Network error' };
  }
}

export async function authFetch(path, options = {}) {
  // Bypass the 401 interceptor for auth endpoints used during relogin or initialization
  const bypassInterceptor = path.includes('sign-in') || path.includes('get-session') || path.includes('sign-out');
  return ncbRequest(buildProxyUrl(`/auth/${path}`), options, bypassInterceptor);
}

export async function dataFetch(path, options = {}) {
  return ncbRequest(buildProxyUrl(`/data/${path}`), options);
}

export async function publicDataFetch(path, options = {}) {
  return ncbRequest(buildProxyUrl(`/public-data/${path}`), options);
}